import { useMemo } from 'react';
import { 
  Users, 
  Mail, 
  Activity, 
  ShieldCheck, 
  TrendingUp, 
  UserPlus,
  Clock,
  ExternalLink,
  UserCheck
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useMembers, useAuditLogs, useInvitations } from '@/api/queries';
import MetricCard from '../MetricCard';

interface DashboardProps {
  workspaceId?: string;
}

export default function EquipeDashboard({ workspaceId }: DashboardProps) {
  const { data: members = [] } = useMembers(workspaceId || '');
  const { data: invitations = [] } = useInvitations(workspaceId || '');
  const { data: auditLogs = [] } = useAuditLogs(workspaceId || '');

  const activeMembers = members.filter((m: any) => m.isActive !== false);
  const activeInvitations = invitations.filter((inv: any) => inv.status === 'PENDING');
  const recentActions = auditLogs.slice(0, 5);
  const recentMembers = activeMembers.slice(-4).reverse();

  const stats = {
    members: activeMembers.length,
    activeInvitations: activeInvitations.length,
    actionsToday: recentActions.length,
    securityIncidents: 0,
  };

  const chartData = useMemo(() => {
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
    const today = new Date();
    const result = days.map((name, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() - ((today.getDay() - i + 7) % 7));
      const dayLogs = auditLogs.filter((log: any) => {
        const logDate = new Date(log.changedAt || log.createdAt);
        return logDate.toDateString() === d.toDateString();
      });
      return { name, actions: dayLogs.length };
    });
    return result;
  }, [auditLogs]);

  if (!workspaceId) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-[#64748B] font-medium">
        Selecione um workspace para visualizar o dashboard.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard 
          title="Membros Ativos" 
          value={stats.members.toString()} 
          icon={Users} 
          trend="API"
          trendUp={true}
          subtext="Membros ativos no workspace"
          color="text-indigo-600"
          bg="bg-indigo-50/80"
          secondaryLabel="Capacidade do Time"
          secondaryValue={`${stats.members} membros`}
          progress={stats.members > 0 ? 100 : 0}
          progressColor="bg-indigo-600"
        />
        <MetricCard 
          title="Convites Pendentes" 
          value={stats.activeInvitations.toString()} 
          icon={Mail} 
          trend="Pendente"
          trendUp={true}
          subtext="Aguardando aceitação"
          color="text-amber-600"
          bg="bg-amber-50/80"
          secondaryLabel="Taxa de Aceite"
          secondaryValue={stats.activeInvitations > 0 ? 'Pendente' : 'Nenhum'}
          progress={stats.activeInvitations > 0 ? 50 : 100}
          progressColor="bg-amber-500"
        />
        <MetricCard 
          title="Ações (24h)" 
          value={stats.actionsToday.toString()} 
          icon={Activity} 
          trend="API"
          trendUp={true}
          subtext="Atividades gravadas hoje"
          color="text-emerald-600"
          bg="bg-emerald-50/80"
          secondaryLabel="Engajamento"
          secondaryValue={stats.actionsToday > 0 ? 'Ativo' : 'Sem atividade'}
          progress={stats.actionsToday > 0 ? 100 : 0}
          progressColor="bg-emerald-500"
        />
        <MetricCard 
          title="Segurança & SSO" 
          value={stats.members > 0 ? 'Ativo' : 'Sem dados'} 
          icon={ShieldCheck} 
          trend="Auditado"
          trendUp={true}
          subtext="Políticas de segurança"
          color="text-blue-600"
          bg="bg-blue-50/80"
          secondaryLabel="Compliance"
          secondaryValue={stats.members > 0 ? 'Ativo' : 'Sem dados'}
          progress={stats.members > 0 ? 100 : 0}
          progressColor="bg-blue-600"
        />
        <MetricCard 
          title="Produtividade" 
          value={stats.actionsToday > 0 ? 'Ativo' : 'Sem dados'} 
          icon={UserCheck} 
          trend="API"
          trendUp={true}
          subtext="Taxa de alocação de squads"
          color="text-purple-600"
          bg="bg-purple-50/80"
          secondaryLabel="Sprints Entregues"
          secondaryValue={stats.actionsToday > 0 ? 'Monitorada' : 'Sem dados'}
          progress={stats.actionsToday > 0 ? 100 : 0}
          progressColor="bg-purple-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white border border-[#0F172A0F] rounded-[32px] p-6 sm:p-7 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-base font-display font-black text-[#0F172A] tracking-tight">Atividade da Equipe</h3>
              <p className="text-xs text-slate-500 font-medium mt-0.5">Frequência de interações no workspace</p>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-700 bg-emerald-50/80 border border-emerald-200/70 px-2.5 py-1 rounded-md uppercase tracking-wider">
              <TrendingUp size={12} /> +24% este período
            </div>
          </div>
          
          <div className="h-[280px] w-full flex items-end justify-between gap-2">
            {chartData.map((item, idx) => (
              <div key={idx} className="flex flex-col items-center gap-2 flex-1">
                <div className="w-full bg-slate-100 rounded-xl relative overflow-hidden" style={{ height: '200px' }}>
                  <div 
                    className="absolute bottom-0 w-full bg-[#0F172A] rounded-xl transition-all duration-500"
                    style={{ height: `${(item.actions / 10) * 100}%` }}
                  />
                </div>
                <span className="text-[10px] font-bold text-slate-500">{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#0F172A0F] rounded-[32px] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-lg font-bold text-[#111111] tracking-tight">Atividade Recente</h3>
            <button className="text-[11px] font-bold text-[#64748B] hover:text-[#111111] uppercase tracking-widest transition-colors flex items-center gap-1.5">
              Ver Tudo <ExternalLink size={14} />
            </button>
          </div>

          <div className="flex flex-col gap-6">
            {recentActions.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#64748B] font-medium">
                Nenhuma atividade recente.
              </div>
            ) : (
              recentActions.map((action: any) => (
                <div key={action.id} className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 border border-[#0F172A05] flex items-center justify-center flex-shrink-0 text-[#111111] font-bold text-xs">
                    {action.actorName?.charAt(0) || 'S'}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <p className="text-sm font-bold text-[#111111]">
                      {action.actorName || 'Sistema'} <span className="font-medium text-[#64748B]">realizou</span> {action.operation || action.action}
                    </p>
                    <p className="text-xs text-[#64748B] font-medium flex items-center gap-1.5">
                      <Clock size={12} /> {new Date(action.changedAt || action.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          <button className="w-full mt-8 py-4 rounded-2xl bg-[#FAFAFA] border border-[#0F172A05] text-sm font-bold text-[#111111] hover:bg-black hover:text-white transition-all flex items-center justify-center gap-2 group">
            <UserPlus size={16} className="group-hover:scale-110 transition-transform" /> Convidar Membro
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#0F172A0F] rounded-[32px] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-base font-display font-black text-[#0F172A] tracking-tight">Membros Recentes</h3>
            <p className="text-xs text-slate-500 font-medium mt-0.5">Últimos profissionais a ingressar no workspace</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {recentMembers.length === 0 ? (
            <div className="col-span-full text-center py-6 text-sm text-[#64748B] font-medium">Nenhum membro recente.</div>
          ) : (
            recentMembers.map((member: any) => (
              <div key={member.id} className="border border-[#0F172A0A] p-4 rounded-2xl flex items-center gap-4 hover:border-black/10 transition-colors bg-[#FAFAFA]/30">
                <div className="w-10 h-10 rounded-xl bg-[#FAFAFB] border border-[#0F172A0A] flex items-center justify-center font-bold text-[#111111] text-xs shadow-sm flex-shrink-0">
                  {member.User?.avatarUrl ? (
                    <img src={member.User.avatarUrl} alt={member.User.fullName || member.User.preferredName} className="w-full h-full object-cover rounded-xl" referrerPolicy="no-referrer" />
                  ) : (
                    (member.User?.fullName || member.User?.preferredName || 'U').charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-xs font-bold text-neutral-900 truncate">{member.User?.fullName || member.User?.preferredName || 'Membro'}</span>
                  <span className="text-[10px] text-neutral-500 font-semibold truncate">{member.User?.email || 'sem email'}</span>
                  <span className="text-[9px] text-[#64748B] font-medium mt-1">Ingresso: {new Date(member.joinedAt).toLocaleDateString()}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
